import React, { useEffect, useState } from 'react';
import { Building, CheckCircle, XCircle, Clock, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AnalyticsAPI, SuppliersAnalytics } from '../../lib/api';

export default function SuppliersAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<SuppliersAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AnalyticsAPI.getSuppliersAnalytics({ period });
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching suppliers analytics:', err);
      setError(err.message || 'Failed to load suppliers analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading suppliers analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Failed to load analytics'}</p>
          <Button onClick={fetchAnalytics}>Retry</Button>
        </div>
      </div>
    );
  }

  const { data } = analytics;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Suppliers Analytics</h1>
          <p className="text-muted-foreground">Supplier performance and activity insights</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <select 
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
          >
            <option value="day">Last 24 hours</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="year">Last year</option>
          </select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Total Suppliers</p>
              <Building className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{data.totalSuppliers}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Active</p>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-2xl font-bold">{data.activeSuppliers}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {data.totalSuppliers > 0 ? ((data.activeSuppliers / data.totalSuppliers) * 100).toFixed(1) : 0}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Verified</p>
              <CheckCircle className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-2xl font-bold">{data.verifiedSuppliers}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {data.totalSuppliers > 0 ? ((data.verifiedSuppliers / data.totalSuppliers) * 100).toFixed(1) : 0}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">With Orders</p>
              <Building className="h-4 w-4 text-purple-600" />
            </div>
            <p className="text-2xl font-bold">{data.suppliersWithOrders}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {data.totalSuppliers > 0 ? ((data.suppliersWithOrders / data.totalSuppliers) * 100).toFixed(1) : 0}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Inactive</p>
              <XCircle className="h-4 w-4 text-red-600" />
            </div>
            <p className="text-2xl font-bold">{data.inactiveSuppliers}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {data.totalSuppliers > 0 ? ((data.inactiveSuppliers / data.totalSuppliers) * 100).toFixed(1) : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Visual representations */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Supplier Status</CardTitle>
            <CardDescription>Active vs Inactive suppliers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Active Suppliers</span>
                  <span className="text-sm text-muted-foreground">
                    {data.activeSuppliers} ({data.totalSuppliers > 0 ? ((data.activeSuppliers / data.totalSuppliers) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="bg-green-500 rounded-full h-3"
                    style={{ width: `${data.totalSuppliers > 0 ? (data.activeSuppliers / data.totalSuppliers) * 100 : 0}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Inactive Suppliers</span>
                  <span className="text-sm text-muted-foreground">
                    {data.inactiveSuppliers} ({data.totalSuppliers > 0 ? ((data.inactiveSuppliers / data.totalSuppliers) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="bg-red-500 rounded-full h-3"
                    style={{ width: `${data.totalSuppliers > 0 ? (data.inactiveSuppliers / data.totalSuppliers) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Verification Status</CardTitle>
            <CardDescription>Supplier verification breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Verified Suppliers</span>
                  <span className="text-sm text-muted-foreground">
                    {data.verifiedSuppliers} ({data.totalSuppliers > 0 ? ((data.verifiedSuppliers / data.totalSuppliers) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="bg-blue-500 rounded-full h-3"
                    style={{ width: `${data.totalSuppliers > 0 ? (data.verifiedSuppliers / data.totalSuppliers) * 100 : 0}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Suppliers with Orders</span>
                  <span className="text-sm text-muted-foreground">
                    {data.suppliersWithOrders} ({data.totalSuppliers > 0 ? ((data.suppliersWithOrders / data.totalSuppliers) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="bg-purple-500 rounded-full h-3"
                    style={{ width: `${data.totalSuppliers > 0 ? (data.suppliersWithOrders / data.totalSuppliers) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary grid */}
      <Card>
        <CardHeader>
          <CardTitle>Supplier Overview</CardTitle>
          <CardDescription>Comprehensive supplier statistics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-lg border p-4">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                <Building className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-2xl font-bold">{data.totalSuppliers}</div>
              <div className="text-xs text-muted-foreground">Total Suppliers</div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-2xl font-bold">{data.activeSuppliers}</div>
              <div className="text-xs text-muted-foreground">Active Suppliers</div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center mb-2">
                <CheckCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-2xl font-bold">{data.verifiedSuppliers}</div>
              <div className="text-xs text-muted-foreground">Verified Suppliers</div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center mb-2">
                <Building className="h-5 w-5 text-indigo-600" />
              </div>
              <div className="text-2xl font-bold">{data.suppliersWithOrders}</div>
              <div className="text-xs text-muted-foreground">With Orders</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
